import { forwardRef, useCallback, useEffect } from 'react';

import { MessageData, ObjectId, OverlayData } from 'types';
import { classes } from 'utils';
import TextContent from './TextContent';
import ImageContent from './ImageContent';
import TriggerContent from './TriggerContent';

import hostIcon from 'assets/images/skydome-icon-blue.svg';
import styles from './Message.module.scss';

type Props = {
  data: MessageData;
  onLoad: (id: ObjectId) => void;
  onAction: (data: OverlayData) => void;
  loading?: boolean;
};

const Message = forwardRef<HTMLDivElement, Props>(
  ({ data, onLoad: loadCallback, onAction, loading }, ref) => {
    const { type, body, date, sender, avatar } = data;

    const onLoad = useCallback(() => loadCallback(data.id), [data.id, loadCallback]);
    const handlers = { onLoad, onAction };

    let content;

    switch (type) {
      case 'text':
      case 'welcome':
        content = <TextContent {...body} {...handlers} />;
        break;

      case 'image':
        content = <ImageContent {...body} {...handlers} />;
        break;

      case 'trigger':
        content = <TriggerContent {...body} {...handlers} />;
        break;

      default:
        return <EmptyMessage {...handlers} />;
    }

    return (
      <div
        ref={ref}
        className={classes(
          styles.message,
          styles[type],
          styles.host,
          loading ? styles.loading : ''
        )}
      >
        <div className={styles.wrapper}>
          <div
            className={styles.avatar}
            style={{ backgroundImage: `url(${avatar || hostIcon})` }}
          />

          <div className={styles.content}>
            <div className={styles.header}>{sender}</div>

            <div className={styles.body}>{content}</div>

            <div className={styles.footer}>
              <span className={styles.arrow} />
              <span className={styles.time}>{date.format('h:mm a')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default Message;

const EmptyMessage = ({ onLoad }: { onLoad: () => void }) => {
  useEffect(() => onLoad(), [onLoad]);
  return null;
};
